import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Plus, Search, Filter, MapPin, Calendar, DollarSign, TrendingUp, Globe, Edit, Save, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFinancasStore } from '@/stores/financas.store';
import { formatCurrency } from '@/lib/utils';

interface Missao {
  id: string;
  tipo: 'local' | 'nacional' | 'internacional' | 'evangelismo' | 'social';
  nome: string;
  descricao: string;
  valor: number;
  dataInicio: string;
  dataFim?: string;
  local: string;
  responsavel: string;
  status: 'planejada' | 'em_andamento' | 'concluida' | 'cancelada';
  observacoes?: string;
}

const MissoesPage: React.FC = () => {
  const navigate = useNavigate();
  const { saldoSeparado } = useFinancasStore();
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [filtros, setFiltros] = useState({
    tipo: '',
    nome: '',
    status: '',
    dataInicio: '',
    dataFim: ''
  });

  // Dados mockados
  const [missoes, setMissoes] = useState<Missao[]>([
    {
      id: '1',
      tipo: 'internacional',
      nome: 'Missão África 2024',
      descricao: 'Evangelização e construção de poços',
      valor: 15000.00,
      dataInicio: '2024-06-01',
      dataFim: '2024-06-30',
      local: 'Moçambique',
      responsavel: 'Pastor Silva',
      status: 'concluida',
      observacoes: 'Missão bem-sucedida'
    },
    {
      id: '2',
      tipo: 'local',
      nome: 'Evangelismo no Centro',
      descricao: 'Evangelização no centro da cidade',
      valor: 2500.00,
      dataInicio: '2024-11-01',
      dataFim: '2024-11-30',
      local: 'Centro da Cidade',
      responsavel: 'Diácono João',
      status: 'em_andamento',
      observacoes: 'Campanha mensal'
    },
    {
      id: '3',
      tipo: 'social',
      nome: 'Natal Solidário',
      descricao: 'Distribuição de cestas básicas',
      valor: 8000.00,
      dataInicio: '2024-12-15',
      dataFim: '2024-12-25',
      local: 'Comunidade Esperança',
      responsavel: 'Irmã Maria',
      status: 'planejada',
      observacoes: 'Ação de Natal'
    }
  ]);

  const totalInvestido = missoes.reduce((acc, missao) => acc + missao.valor, 0);
  const missoesPlanejadas = missoes.filter(m => m.status === 'planejada').length;
  const missoesAndamento = missoes.filter(m => m.status === 'em_andamento').length;
  const missoesConcluidas = missoes.filter(m => m.status === 'concluida').length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planejada': return 'bg-blue-600 dark:bg-blue-500';
      case 'em_andamento': return 'bg-yellow-600 dark:bg-yellow-500';
      case 'concluida': return 'bg-green-600 dark:bg-green-500';
      case 'cancelada': return 'bg-destructive';
      default: return 'bg-muted-foreground';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'planejada': return 'Planejada';
      case 'em_andamento': return 'Em Andamento';
      case 'concluida': return 'Concluída';
      case 'cancelada': return 'Cancelada';
      default: return status;
    }
  };

  const handleStatusClick = (id: string) => {
    setMissoes(prev => prev.map(missao => {
      if (missao.id === id) {
        const statusOrder: Missao['status'][] = ['planejada', 'em_andamento', 'concluida', 'cancelada'];
        const currentIndex = statusOrder.indexOf(missao.status);
        const nextIndex = (currentIndex + 1) % statusOrder.length;
        return { ...missao, status: statusOrder[nextIndex] };
      }
      return missao;
    }));
  };

  const getTipoText = (tipo: string) => {
    switch (tipo) {
      case 'local': return 'Local';
      case 'nacional': return 'Nacional';
      case 'internacional': return 'Internacional';
      case 'evangelismo': return 'Evangelismo';
      case 'social': return 'Ação Social';
      default: return tipo;
    }
  };

  const handleEditValue = (id: string, currentValue: number) => {
    setEditingValue(id);
    setEditValue(currentValue.toString());
  };

  const handleSaveValue = (id: string) => {
    const newValue = parseFloat(editValue);
    if (!isNaN(newValue) && newValue >= 0) {
      setMissoes(prev => prev.map(missao => 
        missao.id === id ? { ...missao, valor: newValue } : missao
      ));
    }
    setEditingValue(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingValue(null);
    setEditValue('');
  };

  const handleDeleteMissao = (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta missão?')) {
      setMissoes(prev => prev.filter(missao => missao.id !== id));
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/financas/administracao')}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Missões</h1>
              <p className="text-muted-foreground">Gestão de missões e atividades evangelísticas</p>
            </div>
          </div>
          <Button onClick={() => navigate('/financas/administracao/missoes/cadastrar')} className="bg-primary hover:bg-primary/90">
            <Plus className="h-4 w-4 mr-2" />
            Cadastrar Missão
          </Button>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Saldo Disponível</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 text-emerald-500 mr-2" />
                <span className="text-2xl font-bold text-emerald-500">
                  {formatCurrency(saldoSeparado.missoes.saldo)}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Investido</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <TrendingUp className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-2xl font-bold text-green-500">
                  {formatCurrency(totalInvestido)}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Planejadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-blue-500 mr-2" />
                <span className="text-2xl font-bold text-blue-500">{missoesPlanejadas}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Em Andamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Globe className="h-4 w-4 text-yellow-500 mr-2" />
                <span className="text-2xl font-bold text-yellow-500">{missoesAndamento}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Concluídas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-2xl font-bold text-green-500">{missoesConcluidas}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros e Tabela Unificados */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtros e Registros de Missões
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <div>
                <Label htmlFor="filtro-tipo">Tipo</Label>
                <Select value={filtros.tipo} onValueChange={(value) => setFiltros({...filtros, tipo: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    <SelectItem value="local">Local</SelectItem>
                    <SelectItem value="nacional">Nacional</SelectItem>
                    <SelectItem value="internacional">Internacional</SelectItem>
                    <SelectItem value="evangelismo">Evangelismo</SelectItem>
                    <SelectItem value="social">Ação Social</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="filtro-nome">Nome</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="filtro-nome"
                    placeholder="Buscar missão..."
                    value={filtros.nome}
                    onChange={(e) => setFiltros({...filtros, nome: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="filtro-status">Status</Label>
                <Select value={filtros.status} onValueChange={(value) => setFiltros({...filtros, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="planejada">Planejada</SelectItem>
                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                    <SelectItem value="concluida">Concluída</SelectItem>
                    <SelectItem value="cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="filtro-data-inicio">Data Inicial</Label>
                <Input
                  id="filtro-data-inicio"
                  type="date"
                  value={filtros.dataInicio}
                  onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
                />
              </div>

              <div>
                <Label htmlFor="filtro-data-fim">Data Final</Label>
                <Input
                  id="filtro-data-fim"
                  type="date"
                  value={filtros.dataFim}
                  onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
                />
              </div>
            </div>

            {/* Tabela de Missões */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">TIPO</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">NOME</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">LOCAL</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">VALOR</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">PERÍODO</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">STATUS</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">RESPONSÁVEL</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">AÇÕES</th>
                  </tr>
                </thead>
                <tbody>
                  {missoes.map((missao) => (
                    <tr key={missao.id} className="border-b">
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-primary border-primary">
                          {getTipoText(missao.tipo)}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium">{missao.nome}</div>
                          <div className="text-sm text-muted-foreground">{missao.descricao}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-muted-foreground mr-2" />
                          {missao.local}
                        </div>
                      </td>
                      <td className="py-3 px-4 font-medium">
                        {editingValue === missao.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="w-32"
                              step="0.01"
                              min="0"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSaveValue(missao.id)}
                              className="text-green-600 hover:text-green-700"
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelEdit}
                              className="text-gray-600 hover:text-gray-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="text-xl font-bold">
                              R$ {missao.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditValue(missao.id, missao.valor)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-muted-foreground mr-2" />
                          <div>
                            <div>{new Date(missao.dataInicio).toLocaleDateString('pt-BR')}</div>
                            {missao.dataFim && (
                              <div className="text-sm text-muted-foreground">
                                até {new Date(missao.dataFim).toLocaleDateString('pt-BR')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge 
                          className={`${getStatusColor(missao.status)} text-white cursor-pointer relative group hover:opacity-80 transition-opacity`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleStatusClick(missao.id);
                          }}
                          title="Clique para mudar status"
                        >
                          {getStatusText(missao.status)}
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Clique para mudar status
                          </span>
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">{missao.responsavel}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteMissao(missao.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Modal de Cadastro */}
        {/* Removido - agora usando card inline */}
      </div>
    </div>
  );
};

export default MissoesPage;
export { MissoesPage };